import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, defer, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, shareReplay, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
    AddressFragment,
    CreateAddressInput,
    GetActiveCustomerQuery,
    GetActiveOrderQuery,
    GetAvailableCountriesQuery,
    GetCustomerAddressesQuery,
    GetEligibleShippingMethodsQuery,
    GetOrderForCheckoutQuery,
    GetOrderShippingDataQuery,
    SetCustomerForOrderMutation,
    SetCustomerForOrderMutationVariables,
    SetShippingAddressMutation,
    SetShippingAddressMutationVariables,
    SetShippingMethodMutation,
    SetShippingMethodMutationVariables,
    TransitionToArrangingPaymentMutation
} from '../../../common/generated-types';
import { GET_ACTIVE_CUSTOMER, GET_AVAILABLE_COUNTRIES, GET_CUSTOMER_ADDRESSES } from '../../../common/graphql/documents.graphql';
import { notNullOrUndefined } from '../../../common/utils/not-null-or-undefined';
import { DataService } from '../../../core/providers/data/data.service';
import { ModalService } from '../../../core/providers/modal/modal.service';
import { NotificationService } from '../../../core/providers/notification/notification.service';
import { StateService } from '../../../core/providers/state/state.service';
import { AddressFormComponent } from '../../../shared/components/address-form/address-form.component';
import { AddressModalComponent } from '../../../shared/components/address-modal/address-modal.component';
import { MapDialogComponent } from '../../../shared/components/map-dialog/map-dialog.component';

import { TranslocoService } from '@jsverse/transloco';
import { GET_ACTIVE_ORDER } from 'src/app/core/providers/active/active.service.graphql';
import { DecodeHtmlPipe } from 'src/app/shared/pipes/decode-html.pipe';
import { CheckoutShippingComponentImports } from '../..';
import { EU_ZONE_CODES } from '../../../core/constants/eu-countries.constants';
import { ActiveService } from '../../../core/providers/active/active.service';
import { CustomerService } from '../../../core/providers/customer/customer.service';
import { TRANSITION_TO_ADDING_ITEMS } from '../checkout-process/checkout-process.graphql';
import {
    GET_DHL_SHOPS,
    GET_DPD_SHOPS,
    GET_ELIGIBLE_SHIPPING_METHODS,
    GET_ORDER_SHIPPING_DATA,
    SET_ADDRESS_TYPE_FOR_ORDER,
    SET_CUSTOMER_FOR_ORDER,
    SET_DHL_SHOP,
    SET_DPD_SHOP,
    SET_ORDER_SHIPPING_ADDRESS,
    SET_SHIPPING_ADDRESS,
    SET_SHIPPING_METHOD,
    TRANSITION_TO_ARRANGING_PAYMENT
} from './checkout-shipping.graphql';

export type AddressFormValue = Pick<AddressFragment, Exclude<keyof AddressFragment, 'country'>> & { countryCode: string; };

const SHIPPING_ORDER = ['DPD_CLASSIC', 'DPD_SHOP', 'DHL', 'DHL_SHOP', 'Store'];
const STORE_MARKER_COLORS = {
    SELECTED: { fill: '#009b05', border: '#018b05' },
    DEFAULT: { fill: '#FBC707', border: '#c49901' },
    USER: { fill: '#ce0000', border: '#ce0000' }
};

@Component({
    standalone: true,
    imports: CheckoutShippingComponentImports,
    selector: 'vsf-checkout-shipping',
    templateUrl: './checkout-shipping.component.html',
    // styleUrls: ['./checkout-shipping.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DialogService, DecodeHtmlPipe]
})
export class CheckoutShippingComponent implements OnInit, OnDestroy, AfterViewInit {
    private dataService = inject(DataService);
    private stateService = inject(StateService);
    private changeDetector = inject(ChangeDetectorRef);
    private modalService = inject(ModalService);
    private notificationService = inject(NotificationService);
    private formBuilder = inject(UntypedFormBuilder);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private translocoService = inject(TranslocoService);
    private cdr = inject(ChangeDetectorRef);
    private activeService = inject(ActiveService);
    private dialogService = inject(DialogService);
    private htmlDecodePipe = inject(DecodeHtmlPipe);
    private customerService = inject(CustomerService);

    @ViewChild('addressForm') addressForm: AddressFormComponent;

    customerAddresses$: Observable<AddressFragment[]>;
    availableCountries$: Observable<GetAvailableCountriesQuery['availableCountries']>;
    eligibleShippingMethods$: Observable<GetEligibleShippingMethodsQuery['eligibleShippingMethods']>;
    shippingAddress$: Observable<NonNullable<GetOrderShippingDataQuery['activeOrder']>['shippingAddress']>;
    signedIn$: Observable<boolean>;
    activeCustomer$: Observable<GetActiveCustomerQuery['activeCustomer']>;
    public shippingMethodId: string | undefined;
    public contactForm: UntypedFormGroup;
    private destroy$ = new Subject<void>();
    cart$: Observable<GetOrderForCheckoutQuery['activeOrder'] | null | undefined>;
    customerAddressId = "";
    private emailError = "";
    dpdStores$: Observable<any[]>;
    dpdStore = new FormControl('', [Validators.required]);
    dhlStores$: Observable<any[]>;
    dhlStore = new FormControl('', [Validators.required]);
    dynamicDialogRef: DynamicDialogRef | undefined | null;
    selectedShippingMethodCode$ = new BehaviorSubject<string>('');
    isAddressValid$!: Observable<boolean>;
    isCompany$: Observable<boolean>;
    isAddressFormValid$: Observable<boolean> = of(true);

    ngOnInit() {
        this.contactForm = this.formBuilder.group({
            firstName: ['', Validators.required],
            lastName: [''],
            emailAddress: ['', Validators.email],
        });
        this.signedIn$ = this.stateService.select(state => state.signedIn);
        this.activeCustomer$ = this.dataService.query<GetActiveCustomerQuery>(GET_ACTIVE_CUSTOMER, {}, 'network-only').pipe(
            map(data => data.activeCustomer),
        );
        this.customerAddresses$ = this.dataService.query<GetCustomerAddressesQuery>(GET_CUSTOMER_ADDRESSES).pipe(
            map(data => data.activeCustomer ? data.activeCustomer.addresses || [] : []),
        );

        this.availableCountries$ = this.dataService.query<GetAvailableCountriesQuery>(GET_AVAILABLE_COUNTRIES).pipe(
            map(data => {
                if (!data || !data.availableCountries) {
                    return [];
                }

                return data.availableCountries.filter(country => 
                    EU_ZONE_CODES.includes(country.code)
                );
            })
        );

        const shippingData$ = this.dataService.query<GetOrderShippingDataQuery>(GET_ORDER_SHIPPING_DATA, {}, 'network-only');
        this.shippingAddress$ = shippingData$.pipe(
            map(data => {
                const address = data.activeOrder?.shippingAddress;
                return {
                    ...address,
                    countryCode: address?.country || ''
                };
            }),
            shareReplay(1)
        );
        this.eligibleShippingMethods$ = this.shippingAddress$.pipe(
            switchMap(() => this.dataService.query<GetEligibleShippingMethodsQuery>(GET_ELIGIBLE_SHIPPING_METHODS, {}, 'network-only')),
            map(data => {
                const methods = data.eligibleShippingMethods;

                return [...methods].sort((a, b) => {
                    const indexA = SHIPPING_ORDER.indexOf((a as any).code);
                    const indexB = SHIPPING_ORDER.indexOf((b as any).code);
                    const priorityA = indexA === -1 ? 99 : indexA;
                    const priorityB = indexB === -1 ? 99 : indexB;

                    return priorityA - priorityB;
                });
            })
        );

        shippingData$.pipe(
            map(data => data.activeOrder && data.activeOrder.customer),
            takeUntil(this.destroy$)
        ).subscribe(customer => {
            if (customer) {
                const isCompany = customer.customFields?.isCompany ?? false;
                this.contactForm.patchValue({
                    firstName: isCompany ? customer.customFields?.companyName : customer.firstName,
                    lastName: customer.lastName,
                    emailAddress: customer.emailAddress,
                }, {emitEvent: false});
            }
        });

        this.cart$ = this.route.data.pipe(switchMap(data => data['activeOrder'] as Observable<GetOrderForCheckoutQuery['activeOrder']>));

        this.customerAddresses$.pipe(takeUntil(this.destroy$)).subscribe(async (data) => {
            const cart: any = await firstValueFrom(this.cart$);
            const typeId = cart?.customFields?.addressTypeId;
            
            // Only for the authorized users.
            // Get address type id from order
            if (typeId !== 0 && typeId != null) { 
                this.customerAddressId = typeId.toString();
                this.setAddress(this.customerAddressId);
            } 
            // or get first address type
            else if (Array.isArray(data) && data.length > 0) {
                this.customerAddressId = data[0].id;
                this.setAddress(this.customerAddressId);
            }
        });

        // Get ID of the first shipping method to select it on the page
        this.eligibleShippingMethods$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
            if (Array.isArray(data) && data.length > 0 && this.shippingMethodId === undefined) {
                this.shippingMethodId = data[0].id; 
            }
        });

        const translationKeys = [
            'checkout.recepient.form.emailAddress.emailIsAlreadyInUse',
        ];
            
        this.translocoService.langChanges$.pipe(
            switchMap(() => {
                const translationObservables = translationKeys.map(key => 
                    this.translocoService.selectTranslate(key)
                );
                return combineLatest(translationObservables);
            }),
            takeUntil(this.destroy$)
        )
        .subscribe((translations: string[]) => {
            [
                this.emailError,
            ] = translations;
            this.cdr.markForCheck();
        });

        this.activeService.activeOrder$.pipe(
            take(1)
        ).subscribe(order => {
            if (order) {
                // get selected shipping method
                this.shippingMethodId = order.shippingLines?.[0]?.shippingMethod?.id;
            }
        });

        this.isCompany$ = this.customerService.isCompany$;

        this.dpdStore.valueChanges.pipe(
            filter(value => value !== null && value !== undefined),
            distinctUntilChanged()
        ).subscribe(value => {
            this.onDpdStoreSelect({ value: value });
        });

        this.dhlStore.valueChanges.pipe(
            filter(value => value !== null && value !== undefined),
            distinctUntilChanged()
        ).subscribe(value => {
            this.onDhlStoreSelect({ value: value });
        });

        // Use the first eligible shipping method as default
        // if the user has not selected one yet
        if(!this.shippingMethodId){
            this.eligibleShippingMethods$.subscribe(data => {
                if (data?.[0]?.id) {
                    this.setShippingMethod(data[0]?.id);
                }
            });
        }
    }

    ngAfterViewInit() {
        const addressChanges$: Observable<{pc: string, cc: string}> = defer(() => {
            const form = this.addressForm.addressForm;
            return form.valueChanges.pipe(
                startWith(form.value)
            );
        }).pipe(
            map(({ postalCode, countryCode }) => ({
                pc: (postalCode ?? '').trim(),
                cc: (countryCode ?? '').trim()
            })),
            distinctUntilChanged((prev, curr) => prev.pc === curr.pc && prev.cc === curr.cc),
            debounceTime(100),
            // tap(data => console.log('Address changes detected:', data)),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        // Disable/enable drop-down if postal code or country is empty
        this.isAddressValid$ = addressChanges$.pipe(
            map(address => !!address.pc && !!address.cc),
            distinctUntilChanged(),
            tap(isValid => {
                isValid 
                    ? this.dpdStore.enable({ emitEvent: false }) 
                    : this.dpdStore.disable({ emitEvent: false });
            }),
            shareReplay(1)
        );

        // define shipping metod from order
        this.cart$.pipe(
            take(1),
            // get the first shippingLine
            map(cart => cart?.shippingLines?.find(() => true)?.shippingMethod),
            filter(method => !!method)
        ).subscribe((method: any) => {
            if (method.code) {
                this.selectedShippingMethodCode$.next(method.code);
                this.shippingMethodId = method.id;
            }
        });

        this.dpdStores$ = combineLatest([
            this.selectedShippingMethodCode$.asObservable(), 
            addressChanges$
        ]).pipe(
            // Prevent the calling switchMap() if data was not updated
            distinctUntilChanged((prev, curr) => {
                const [prevMethod, prevAddr] = prev;
                const [currMethod, currAddr] = curr;
                return prevMethod === currMethod && 
                    prevAddr.pc === currAddr.pc && 
                    prevAddr.cc === currAddr.cc;
            }),
            switchMap(([methodCode, address]) => {
                
                if (methodCode === 'DPD_SHOP' && address.pc !=='' && address.cc !=='') { 
                    // console.log("Call API = ", methodCode, address);
                    
                    return this.dataService.query<any>(GET_DPD_SHOPS, {
                        args: {
                            postalCode: address.pc,
                            countryCode: address.cc,
                            limit: 20,
                            hideClosed: true
                        }
                    }).pipe(
                        map(data => data.dpdParcelShops.map((store: { company: string; city: string; street: string; houseNumber: string; }) => ({
                            ...store,
                            name: store.company,
                            label: `${this.htmlDecodePipe.transform(store.company + " - " + store.city + ", " + store.street + ", " + store.houseNumber)}`,
                        })))
                    );
                }
                return of([]);

                // // Mocked, remove after testing
                // if (methodCode === 'DPD_SHOP' && address.pc !=='' && address.cc !=='') { 
                //     console.log("Call mock = ", methodCode, address);
                //     return of([
                //         { "__typename": "DpdParcelShop", "parcelShopId": "419469", "company": "Leder-Weil1", "street": "Neuer Weg", "houseNumber": "60", "postalCode": "60388", "city": "Frankfurt am Main", "latitude": 50.149915, "longitude": 8.752322 },
                //         { "__typename": "DpdParcelShop", "parcelShopId": "305286", "company": "Style1 &amp; Cutting Group Rahn", "street": "Triebstr. &amp; ", "houseNumber": "21 &amp;", "postalCode": "60388", "city": "Frankfurt am Main", "latitude": 50.1470583, "longitude": 8.7542271 },
                //         { "__typename": "DpdParcelShop", "parcelShopId": "342817", "company": "NRS1 Impex Asiatische Lebensmittel", "street": "Victor-Slotosch-Str.", "houseNumber": "8", "postalCode": "60388", "city": "Frankfurt am Main", "latitude": 50.144767, "longitude": 8.750187 },
                //     ]).pipe(
                //         map(stores => stores.map(store => ({
                //             ...store,
                //             label: `${this.htmlDecodePipe.transform(store.company + " - " + store.city + ", " + store.street + ", " + store.houseNumber)}`,
                //         })))
                //     );
                // }
                // return of([]);
            })
        );

        // set DPD and DHL store Id as selected item in dropdown
        this.cart$
        .pipe(takeUntil(this.destroy$))
        .subscribe((cart: any) => {
            const dpdId = cart?.customFields?.dpdParcelShopId;
            if (dpdId && this.dpdStore.value !== dpdId) {
                this.dpdStore.setValue(dpdId, { emitEvent: false });
            }
            const dhlId = cart?.customFields?.dhlShopData;
            if (dhlId && this.dhlStore.value !== dhlId) {
                this.dhlStore.setValue(dhlId, { emitEvent: false });
            }
        });

        this.dhlStores$ = combineLatest([
            this.selectedShippingMethodCode$.asObservable(), 
            addressChanges$
        ]).pipe(
            // Prevent the calling switchMap() if data was not updated
            distinctUntilChanged((prev, curr) => {
                const [prevMethod, prevAddr] = prev;
                const [currMethod, currAddr] = curr;
                return prevMethod === currMethod && 
                    prevAddr.pc === currAddr.pc && 
                    prevAddr.cc === currAddr.cc;
            }),
            switchMap(([methodCode, address]) => {
                
                if (methodCode === 'DHL_SHOP' && address.pc !=='' && address.cc !=='') { 
                    // console.log("Call API = ", methodCode, address);
                    
                    return this.dataService.query<any>(GET_DHL_SHOPS, {
                        args: {
                            postalCode: address.pc,
                            countryCode: address.cc,
                            limit: 20
                        }
                    }).pipe(
                        map(data => data.dhlServicePoints.map((store: { name: string; city: string; street: string; retailID:string; }) => ({
                            ...store,
                            label: `${this.htmlDecodePipe.transform(store.name + " - " + store.city + ", " + store.street)}`,
                            parcelShopId: store.retailID,
                        }))),
                        //tap(data => console.log("data = ", data))
                    );
                }
                return of([]);

                // // Mocked, remove after testing
                // if (methodCode === 'DPD_SHOP' && address.pc !=='' && address.cc !=='') { 
                //     console.log("Call mock = ", methodCode, address);
                //     return of([
                //         { "__typename": "DpdParcelShop", "parcelShopId": "419469", "company": "Leder-Weil1", "street": "Neuer Weg", "houseNumber": "60", "postalCode": "60388", "city": "Frankfurt am Main", "latitude": 50.149915, "longitude": 8.752322 },
                //         { "__typename": "DpdParcelShop", "parcelShopId": "305286", "company": "Style1 &amp; Cutting Group Rahn", "street": "Triebstr. &amp; ", "houseNumber": "21 &amp;", "postalCode": "60388", "city": "Frankfurt am Main", "latitude": 50.1470583, "longitude": 8.7542271 },
                //         { "__typename": "DpdParcelShop", "parcelShopId": "342817", "company": "NRS1 Impex Asiatische Lebensmittel", "street": "Victor-Slotosch-Str.", "houseNumber": "8", "postalCode": "60388", "city": "Frankfurt am Main", "latitude": 50.144767, "longitude": 8.750187 },
                //     ]).pipe(
                //         map(stores => stores.map(store => ({
                //             ...store,
                //             label: `${this.htmlDecodePipe.transform(store.company + " - " + store.city + ", " + store.street + ", " + store.houseNumber)}`,
                //         })))
                //     );
                // }
                // return of([]);
            })
        );

        const form = this.addressForm.addressForm;
        this.isAddressFormValid$ = form.valueChanges.pipe(
            startWith(form.value),
            map(value => !!(value.streetLine1 && value.city && value.postalCode))
        );
        this.changeDetector.detectChanges();
    }

    onShippingMethodSelect(code: string) {
        this.selectedShippingMethodCode$.next(code);
    }

    getLines(address: AddressFragment): string[] {
        return [
            address.fullName,
            address.company,
            address.streetLine1,
            address.streetLine2,
            address.province,
            address.postalCode,
            address.country.name,
        ].filter(notNullOrUndefined);
    }

    createAddress() {
        this.modalService.fromComponent(AddressModalComponent, {
            locals: {
                title: 'Create new address',
            },
            closable: true,
        }).pipe(
            switchMap(() => this.dataService.query<GetCustomerAddressesQuery>(GET_CUSTOMER_ADDRESSES, {}, 'network-only')),
        )
            .subscribe();
    }

    editAddress(address: AddressFragment) {
        this.addressForm.addressForm.patchValue({...address, countryCode: address.country.code});
    }

    onCustomerFormBlur() {
        this.setCustomerForOrder()?.subscribe();
    }

    onAddressFormBlur(addressForm: UntypedFormGroup) {
        if (addressForm.dirty && addressForm.valid) {
            this.setShippingAddress(addressForm.value);
        }
    }

    setShippingAddress(value: AddressFormValue | AddressFragment) {
        const input = this.valueToAddressInput(value);
        this.dataService.mutate<SetShippingAddressMutation, SetShippingAddressMutationVariables>(SET_SHIPPING_ADDRESS, {
            input,
        }).subscribe(data => {
            const order = data.setOrderShippingAddress;
                if (order?.__typename === 'Order') {
                    this.activeService.setActiveOrder(order as any);
                }
            this.changeDetector.markForCheck();
        });
    }

    setShippingMethod(id: string) {
        this.shippingMethodId = id;
        
        this.dataService.mutate<SetShippingMethodMutation, SetShippingMethodMutationVariables>(
            SET_SHIPPING_METHOD, 
            { id }
        ).subscribe({
            next: (result) => {
                const order = result.setOrderShippingMethod;
                if (order?.__typename === 'Order') {
                    this.activeService.setActiveOrder(order as any);
                }
                this.changeDetector.markForCheck();
            },
            error: (err) => {
                console.error('Error setting shipping method:', err);
                this.notificationService.error('Festlegen der Versandart fehlgeschlagen').subscribe();
            }
        });
    }

    onDpdStoreSelect(event: any) {
        const selectedShopId = event.value;
        if (selectedShopId) {
            this.setDPDShop(selectedShopId);
        }
    }

    setDPDShop(id: string) {
        this.dataService.mutate(SET_DPD_SHOP, {
            input: {
                customFields: {
                    dpdParcelShopId: id
                }
            }
        }).subscribe({
            next: (result) => {
                // console.log('DPD store was saved sucessfully');
            },
            error: (error) => {
                console.error('DPD store saving fault: ', error);
            }
        });
    }

    onDhlStoreSelect(event: any) {
        const selectedShopId = event.value;
        if (selectedShopId) {
            this.setDhlShop(selectedShopId);
        }
    }

    setDhlShop(id: string) {
        this.dataService.mutate(SET_DHL_SHOP, {
            input: {
                customFields: {
                    dhlShopData: id
                }
            }
        }).subscribe({
            next: (result) => {
                // console.log('DPD store was saved sucessfully');
            },
            error: (error) => {
                console.error('DHL store saving fault: ', error);
            }
        });
    }

    proceedToPayment() {
        const shippingMethodId = this.shippingMethodId;
        if (shippingMethodId) {
            this.stateService.select(state => state.signedIn).pipe(
                take(1),
                mergeMap(signedIn =>
                    // get order from DB and update local storage
                    this.dataService.query<GetActiveOrderQuery>(GET_ACTIVE_ORDER).pipe(
                        take(1),
                        map(data => ({ signedIn, order: data.activeOrder })),
                        tap(({ order }) => order && this.activeService.setActiveOrder(order as any))
                    )
                ),
                mergeMap(signedIn => !signedIn ? this.setCustomerForOrder() || of({}) : of({})),
                mergeMap(() => {
                    // Set order in 'AddingItems', because it must be in this state for shipping method update
                    const order = (this.activeService as any).activeOrderSubject.value;
                    if (order?.state === 'ArrangingPayment') {
                        return this.dataService.mutate(TRANSITION_TO_ADDING_ITEMS).pipe(
                            map(res => res.transitionOrderToState)
                        );
                    }
                    return of(order);
                }),
                mergeMap(() =>
                    // Shipping method update
                    this.dataService.mutate<SetShippingMethodMutation, SetShippingMethodMutationVariables>(SET_SHIPPING_METHOD, {
                        id: shippingMethodId,
                    }),
                ),
                mergeMap((result) => {
                    // Set the order to ArrangingPayment
                    const order = result.setOrderShippingMethod;
                    if (order?.__typename === 'Order' && order?.state === 'ArrangingPayment') {
                        return of({ transitionOrderToState: order });
                    }

                    return this.dataService.mutate<TransitionToArrangingPaymentMutation>(TRANSITION_TO_ARRANGING_PAYMENT);
                }),
            ).subscribe({
                next: (result) => {
                    const order = result.transitionOrderToState;
                    switch (order?.__typename) {
                        case 'Order':
                            this.activeService.setActiveOrder(order as GetActiveOrderQuery['activeOrder']);
                            break;
                        case 'OrderStateTransitionError':
                    }
                    this.router.navigate(['/checkout/payment'], {relativeTo: this.route});
                },
                error: (error) => {
                    console.error('GraphQL ERROR:', error);
                }
            });
        }
    }

    getId(method: { id: string }) {
        return method.id;
    }

    getLogo(name: string): string | null {
        const logos: Record<string, string> = {
            DHL:        'assets/icons/dhl_logo.png',
            DHL_SHOP:   'assets/icons/dhl_logo.png',
            DPD_CLASSIC:'assets/icons/dpd_logo.png',
            DPD_SHOP:   'assets/icons/dpd_logo.png',
            Store:      'assets/icons/store_logo.png',
        };
        return logos[name] ?? null;
    }

    private setCustomerForOrder() {
        if (this.contactForm.valid) {
            return this.dataService.mutate<SetCustomerForOrderMutation, SetCustomerForOrderMutationVariables>(SET_CUSTOMER_FOR_ORDER, {
                input: this.contactForm.value,
            }).pipe(
                tap(({setCustomerForOrder}) => {

                    // more detailed error message about entered email
                    let message = (setCustomerForOrder as any).message;
                    if (message && message.toLowerCase().includes("email address")) {
                        message = this.emailError;
                    }

                    if (setCustomerForOrder && setCustomerForOrder.__typename !== 'Order') {
                        this.notificationService.error("Unbekannter Fehler (598)").subscribe();
                    }
                })
            );
        }
        return null;
    }

    private valueToAddressInput(value: AddressFormValue | AddressFragment): CreateAddressInput {
        return {
            city: value.city || '',
            company: value.company || '',
            countryCode: this.isFormValue(value) ? value.countryCode : value.country.code,
            defaultBillingAddress: value.defaultBillingAddress,
            defaultShippingAddress: value.defaultShippingAddress,
            fullName: value.fullName || '',
            phoneNumber: value.phoneNumber || '',
            postalCode: value.postalCode || '',
            province: value.province || '',
            streetLine1: value.streetLine1 || '',
            streetLine2: value.streetLine2 || '',
        };
    }

    private isFormValue(input: AddressFormValue | AddressFragment): input is AddressFormValue {
        return typeof (input as any).countryCode === 'string';
    }

    setAddress(id: string) {
        this.customerAddressId = id;
        const address$ = this.customerAddresses$.pipe(
            filter(addresses => addresses !== null && addresses.length > 0),
            map(addresses => addresses.find(x => x.id === id) || null),
            filter(address => address !== null),
        );
        this.applyBillingAsShipping(address$);

        this.dataService.mutate(SET_ADDRESS_TYPE_FOR_ORDER, {
            input: {
                customFields: {
                    addressTypeId: Number(id) || 0 
                }
            }
        }).subscribe();
    }

    applyBillingAsShipping(address$: Observable<AddressFragment | null>) {
        address$
            .pipe(take(1))
            .subscribe(billing => {
                if (!billing) {
                    console.warn('No address found');
                    return;
                }

                const input = {
                    fullName: billing.fullName,
                    streetLine1: billing.streetLine1,
                    streetLine2: billing.streetLine2 || '',
                    city: billing.city,
                    postalCode: billing.postalCode,
                    countryCode: billing.country.code,
                    phoneNumber: billing.phoneNumber || '',
                    company: billing.company || '',
                    province: billing.province || '',
                };

                this.dataService
                    .mutate(SET_ORDER_SHIPPING_ADDRESS, { input })
                    .subscribe({
                        next: (result) => {
                            // Useful to uncomment for the testing
                            //console.log('Shipping address updated:', result);
                        },
                        error: (errorResponse) => {
                            console.error('SET_ORDER_SHIPPING_ADDRESS failed:', errorResponse);
                            if (errorResponse.error && errorResponse.error.errors) {
                                console.error('GraphQL error:', errorResponse.error.errors);
                            }
                        },
                    });
            });
    }

    showMap(storeId: any, provider: 'dhl' | 'dpd') {
        const configMap = {
            dhl: {
                stores$: this.dhlStores$,
                control: this.dhlStore,
                shopId: 'retailID',
                header: 'DHL ServicePoint'
            },
            dpd: {
                stores$: this.dpdStores$,
                control: this.dpdStore,
                shopId: 'parcelShopId',
                header: 'DPD Pickup'
            }
        };

        const config = configMap[provider];

        const getSvgIconUrl = (color: string, borderColor: string): string => {
            const svg = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40">
                    <path 
                        fill="${color}" 
                        stroke="${borderColor}" 
                        stroke-width="0.5" 
                        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                    />
                    <circle fill="white" cx="12" cy="9" r="2.5"/>
                </svg>`;
            return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
        };

        config.stores$.pipe(take(1)).subscribe((stores: any[]) => {
            if (!stores || stores.length === 0) return;

            const allStores = stores.map(s => {
                const isSelected = s[config.shopId] === storeId;
                const color = isSelected ? STORE_MARKER_COLORS.SELECTED.fill : STORE_MARKER_COLORS.DEFAULT.fill;
                const borderColor = isSelected ? STORE_MARKER_COLORS.SELECTED.border : STORE_MARKER_COLORS.DEFAULT.border;

                return {
                    lat: s.latitude,
                    lng: s.longitude,
                    label: s.label,
                    city: s.city,
                    company: s.name,
                    houseNumber: s.houseNumber || "",
                    parcelShopId: s[config.shopId],
                    postalCode: s.postalCode,
                    street: s.street,
                    country: s.country || "",
                    isSelectedShop: isSelected,
                    isUserLocation: false,
                    options: {
                        icon: {
                            url: getSvgIconUrl(color, borderColor),
                            scaledSize: { width: 40, height: 40 }
                        },
                        animation: isSelected ? 1 : null
                    }
                };
            });
            
            this.shippingAddress$.pipe(take(1)).subscribe(address => {
                if (address) {

                    allStores.push({ 
                        lat: 0, // Will defined in openMapDialog component
                        lng: 0, // Will defined in openMapDialog component
                        label: `${address.streetLine1} ${address.streetLine2}, ${address.postalCode} ${address.city} ${address.country}`,
                        city: address.city || "",
                        company: "",
                        houseNumber: address.streetLine2 || "",
                        parcelShopId: "",
                        postalCode: address.postalCode || "",
                        street: address.streetLine1 || "",
                        country: address.country || "",
                        isSelectedShop: false,
                        isUserLocation: true,
                        options: {
                            icon: {
                                url: getSvgIconUrl(STORE_MARKER_COLORS.USER.fill, STORE_MARKER_COLORS.USER.border),
                                scaledSize: { width: 40, height: 40 }
                            },
                            animation: null
                        }
                    });
                }
                
                const selectedStore = storeId 
                        ? stores.find(s => s.retailID === storeId) 
                        : null;

                this.openMapDialog(allStores, selectedStore, config);
            });
        });
    }

    private openMapDialog(stores: any[], selectedStore: any, config: any) {
        if (!config.control) {
            console.error('Target control is missing!');
            return;
        }

        const ref = this.dialogService.open(MapDialogComponent, {
            header: config.header,
            width: '95vw',
            height: '95vh',
            contentStyle: { 'flex': '1 1 auto' },
            styleClass: 'map-dialog-wrapper',
            baseZIndex: 10000,
            draggable: false,
            closable: true,
            modal: true,
            data: {
                points: stores,
                selectedPoint: selectedStore,
            }
        });

        ref?.onClose.subscribe((selectedStore: any) => {
            if (selectedStore) {
                config.control.setValue(selectedStore.parcelShopId, { emitEvent: true });
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
